import React, { useState } from 'react'
import { User, Globe, Instagram, Facebook, Linkedin, Youtube, MapPin, ChevronRight, Mail, Award, Download, Video, BookOpen, X } from 'lucide-react'
import { XIcon } from '../../icons/XIcon'

// Helper function to parse markdown-like formatting
const parseFormattedText = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Split by lines to handle bullets
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    let content: React.ReactNode = line;
    
    // Check for bullet points (lines starting with - or *)
    const isBullet = line.trim().startsWith('- ') || line.trim().startsWith('* ');
    if (isBullet) {
      content = line.trim().substring(2); // Remove the bullet marker
    }
    
    // Parse bold and italic
    let parts: React.ReactNode[] = [content as string];
    
    // Parse bold (**text** or __text__)
    parts = parts.flatMap((part) => {
      if (typeof part !== 'string') return part;
      const boldRegex = /\*\*(.+?)\*\*|__(.+?)__/g;
      const matches = [];
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(part)) !== null) {
        if (match.index > lastIndex) {
          matches.push(part.substring(lastIndex, match.index));
        }
        matches.push(<strong key={`bold-${lineIndex}-${match.index}`}>{match[1] || match[2]}</strong>);
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < part.length) {
        matches.push(part.substring(lastIndex));
      }
      
      return matches.length > 0 ? matches : part;
    });
    
    // Parse italic (*text* or _text_)
    parts = parts.flatMap((part) => {
      if (typeof part !== 'string') return part;
      const italicRegex = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g;
      const matches = [];
      let lastIndex = 0;
      let match;
      
      while ((match = italicRegex.exec(part)) !== null) {
        if (match.index > lastIndex) {
          matches.push(part.substring(lastIndex, match.index));
        }
        matches.push(<em key={`italic-${lineIndex}-${match.index}`}>{match[1] || match[2]}</em>);
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < part.length) {
        matches.push(part.substring(lastIndex));
      }
      
      return matches.length > 0 ? matches : part;
    });
    
    // Wrap in appropriate element
    if (isBullet) {
      return (
        <li key={lineIndex} className="ml-4 list-disc">
          {parts}
        </li>
      );
    } else if (line.trim() === '') {
      return <br key={lineIndex} />;
    } else {
      return (
        <span key={lineIndex}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    }
  });
}

interface AuthorPreviewContentProps {
  data: {
    name?: string
    profession?: string
    shortBio?: string
    fullBio?: string
    profileImageUrl?: string
    socialLinks?: {
      website?: string
      instagram?: string
      facebook?: string
      twitter?: string
      linkedin?: string
      tiktok?: string
    }
    contact?: {
      email?: string
      agent?: string
    }
    awards?: string[]
    pressKitUrl?: string
    videoUrl?: string
    newsletterEnabled?: boolean
    companyPosition?: 'top' | 'bottom'
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      branding?: string
      name?: string
      bio?: string
      awards?: string
    }
    animationTrigger?: number
  }
  branding: {
    companyName?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
    textColor?: string
    primaryColor?: string
    secondaryColor?: string
    wrapperColor?: string
    wrapperOpacity?: number
  }
  bgGradient?: string
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean
}

export const AuthorPreviewContent: React.FC<AuthorPreviewContentProps> = ({ 
  data, 
  branding,
  bgGradient = 'linear-gradient(to bottom right, #059669, #047857)',
  colorMode = 'gradient',
  isPreview = true
}) => {
  const [showExpandedBio, setShowExpandedBio] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  
  // Helper function to get profession text size based on name size
  const getProfessionTextSize = (nameSize: string): string => {
    const sizeMap: Record<string, string> = {
      'text-xs': 'text-xs',      // If name is xs, profession stays xs
      'text-sm': 'text-xs',      // sm -> xs
      'text-base': 'text-xs',    // base -> xs
      'text-lg': 'text-xs',      // lg -> xs
      'text-xl': 'text-xs',      // xl -> xs (4 steps down but we stop at xs)
      'text-2xl': 'text-xs',     // 2xl -> xs (4 steps: 2xl->xl->lg->base->sm->xs)
      'text-3xl': 'text-sm',     // 3xl -> sm (4 steps: 3xl->2xl->xl->lg->base->sm)
      'text-4xl': 'text-base',   // 4xl -> base (4 steps: 4xl->3xl->2xl->xl->lg->base)
      'text-5xl': 'text-lg',     // 5xl -> lg (4 steps: 5xl->4xl->3xl->2xl->xl->lg)
      'text-6xl': 'text-xl',     // 6xl -> xl (4 steps: 6xl->5xl->4xl->3xl->2xl->xl)
    }
    return sizeMap[nameSize] || 'text-xs'
  }

  const nameTextSize = data.textSizes?.name || 'text-3xl'
  const professionTextSize = getProfessionTextSize(nameTextSize)

  // Load Google Fonts if needed
  React.useEffect(() => {
    const fonts = new Set([data.fonts?.heading, data.fonts?.body].filter(Boolean))
    fonts.forEach(font => {
      if (font && font !== 'Inter') {
        const link = document.createElement('link')
        link.href = `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}:wght@400;600;700&display=swap`
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
    })
  }, [data.fonts])

  // Social media icons mapping
  const socialIcons: Record<string, any> = {
    website: Globe,
    instagram: Instagram,
    facebook: Facebook,
    twitter: XIcon,
    linkedin: Linkedin,
    youtube: Youtube,
    location: MapPin
  }


  // Extract YouTube ID from URL
  const getYoutubeId = (url: string) => {
    if (!url) return null
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
    return match ? match[1] : null
  }

  const youtubeId = data.videoUrl ? getYoutubeId(data.videoUrl) : null

  // Shared profile content component
  const ProfileContent = () => (
    <>
      {/* Profile Image */}
      <div className="mb-6">
        {data.profileImageUrl ? (
          <img
            src={data.profileImageUrl}
            alt={data.name || 'Author'}
            className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg border-4 border-white/20"
          />
        ) : (
          <div className="w-32 h-32 rounded-full mx-auto shadow-lg border-4 border-white/20 flex items-center justify-center" 
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
            <User className="w-16 h-16" style={{ color: branding.textColor || 'white' }} />
          </div>
        )}
      </div>

      {/* Name */}
      {data.name && (
        <h1 className={`${data.textSizes?.name || 'text-3xl'} font-bold mb-1`} style={{ 
          color: branding.textColor || 'white',
          fontFamily: data.fonts?.heading || 'Inter'
        }}>
          {data.name}
        </h1>
      )}
      
      {/* Profession/Title */}
      {data.profession && (
        <p className={`${professionTextSize} opacity-90 mb-3`} style={{ 
          color: branding.textColor || 'white',
          fontFamily: data.fonts?.body || 'Inter'
        }}>
          {data.profession}
        </p>
      )}
      
      {/* Social Media Icons */}
      {data.socialLinks && Object.keys(data.socialLinks).some(key => data.socialLinks![key as keyof typeof data.socialLinks]) && (
        <div className="flex justify-center gap-2 mt-4">
          {Object.entries(data.socialLinks).map(([platform, url]) => {
            if (!url) return null
            const Icon = socialIcons[platform]
            if (!Icon) return null
            
            const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
              ? url 
              : `https://${url}`;
            
            return (
              <a
                key={platform}
                href={formattedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-full hover:opacity-80 transition-all"
                style={{ color: branding.textColor || 'white', backgroundColor: branding.primaryColor }}
              >
                <Icon className="w-3.5 h-3.5" />
              </a>
            )
          })}
        </div>
      )}
    </>
  )

  // Main content layout - shared between both modes
  const MainContent = () => (
    <>
      <div className="px-6 pt-6">
        {/* Company name at top */}
        {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
          <div className="text-center mb-4">
            <h2 className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
              {branding.companyName}
            </h2>
          </div>
        )}

        {/* Profile, Name and Title Wrapper */}
        <div className="text-center">
          <div 
            className="backdrop-blur-sm rounded-lg p-6 max-w-sm mx-auto"
            style={{
              backgroundColor: branding.wrapperColor || 'rgba(255, 255, 255, 0.1)',
              color: branding.textColor || 'white',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 10px 25px -5px rgba(0,0,0,0.3), 0 20px 40px -10px rgba(0,0,0,0.2)'
            }}
          >
            <ProfileContent />
          </div>
        </div>
        
        {/* Short Bio with wrapper */}
        {data.shortBio && (
          <div className="mt-4">
            <div 
              className="max-w-sm mx-auto backdrop-blur-sm rounded-lg p-6"
              style={{
                backgroundColor: branding.wrapperColor ? 
                  `rgba(${parseInt(branding.wrapperColor.slice(1,3), 16)}, ${parseInt(branding.wrapperColor.slice(3,5), 16)}, ${parseInt(branding.wrapperColor.slice(5,7), 16)}, ${branding.wrapperOpacity || 0.5})` : 
                  'rgba(255, 255, 255, 0.1)',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 10px 25px -5px rgba(0,0,0,0.3), 0 20px 40px -10px rgba(0,0,0,0.2)'
              }}
            >
              <h3 className={`${data.textSizes?.bio || 'text-base'} font-semibold mb-2`} style={{ 
                color: branding.textColor || 'white',
                fontFamily: data.fonts?.heading || 'Inter'
              }}>
                Biografie
              </h3>
              <p className={`${data.textSizes?.bio || 'text-base'} whitespace-pre-wrap opacity-90`} style={{ 
                color: branding.textColor || 'white',
                fontFamily: data.fonts?.body || 'Inter'
              }}>
                {data.shortBio}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Lower section content */}
      <div className="px-6 pt-4 pb-8">
        <div className="max-w-sm mx-auto">
          {/* Action Buttons */}
          <div className="space-y-2 mb-4">
            {/* Full Bio Button */}
            {data.fullBio && (
              <button
                onClick={() => setShowExpandedBio(true)}
                className="w-full py-3 px-6 font-medium transform transition-transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
                style={{
                  backgroundColor: branding.primaryColor,
                  color: branding.textColor || 'white',
                  borderRadius: '0.5rem',
                  fontFamily: data.fonts?.body || 'Inter'
                }}
              >
                <BookOpen className="w-5 h-5" />
                <span className={data.textSizes?.bio || 'text-base'}>Vollständige Biografie</span>
              </button>
            )}

            {/* Video Button */}
            {youtubeId && (
              <button
                onClick={() => setShowVideoModal(true)}
                className="w-full py-3 px-6 font-medium transform transition-transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
                style={{
                  backgroundColor: branding.primaryColor,
                  color: branding.textColor || 'white',
                  borderRadius: '0.5rem',
                  fontFamily: data.fonts?.body || 'Inter'
                }}
              >
                <Video className="w-5 h-5" />
                <span className={data.textSizes?.bio || 'text-base'}>Video ansehen</span>
              </button>
            )}

            {/* Press Kit Button */}
            {data.pressKitUrl && (
              <a
                href={data.pressKitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 px-6 font-medium transform transition-transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg block"
                style={{
                  backgroundColor: branding.primaryColor,
                  color: branding.textColor || 'white',
                  borderRadius: '0.5rem',
                  fontFamily: data.fonts?.body || 'Inter'
                }}
              >
                <Download className="w-5 h-5" />
                <span className={data.textSizes?.bio || 'text-base'}>Presse-Kit herunterladen</span>
              </a>
            )}
          </div>

          {/* Awards */}
          {data.awards && data.awards.length > 0 && (
            <div className="backdrop-blur-sm rounded-lg p-3 mb-4" style={{
              backgroundColor: branding.wrapperColor || 'rgba(255, 255, 255, 0.1)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 10px 25px -5px rgba(0,0,0,0.3), 0 20px 40px -10px rgba(0,0,0,0.2)'
            }}>
              <h3 className={`${data.textSizes?.bio || 'text-base'} font-semibold mb-2 flex items-center gap-2`} 
                style={{ color: branding.textColor || 'white', fontFamily: data.fonts?.heading || 'Inter' }}>
                <Award className="w-4 h-4" />
                Auszeichnungen
              </h3>
              <ul className="space-y-1">
                {data.awards.slice(0, 3).map((award, index) => (
                  <li key={index} className={`${data.textSizes?.bio || 'text-base'}`} 
                    style={{ color: branding.textColor || 'white', fontFamily: data.fonts?.body || 'Inter' }}>
                    • {award}
                  </li>
                ))}
                {data.awards.length > 3 && (
                  <li className={`${data.textSizes?.bio || 'text-base'} opacity-70 italic`} 
                    style={{ color: branding.textColor || 'white', fontFamily: data.fonts?.body || 'Inter' }}>
                    und {data.awards.length - 3} weitere...
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Company name at bottom */}
          {branding.brandingType !== 'none' && data.companyPosition === 'bottom' && (
            <div className="text-center">
              {branding.brandingType === 'beyondpages' ? (
                <p className="text-sm opacity-80" style={{ color: '#1A1A1A', fontFamily: data.fonts?.body || 'Inter' }}>
                  Powered by BeyondPages
                </p>
              ) : branding.brandingType === 'custom' && branding.companyName ? (
                <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ color: '#1A1A1A', fontFamily: data.fonts?.body || 'Inter' }}>
                  {branding.companyName}
                </h2>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colorMode === 'gradient' ? (
          // Gradient mode - single continuous gradient
          <div className="min-h-full relative" style={{
            background: `linear-gradient(to bottom, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
          }}>
            <MainContent />
          </div>
        ) : (
          // Structured mode - uses same layout with background split
          <div className="min-h-full relative">
            {/* Fixed background split */}
            <div className="absolute inset-0 flex flex-col">
              <div style={{ backgroundColor: branding.primaryColor, height: '250px' }} />
              <div className="flex-1" style={{ backgroundColor: branding.secondaryColor }} />
            </div>
            
            {/* Content layer - using MainContent */}
            <div className="relative z-10">
              <MainContent />
            </div>
          </div>
        )}
      </div>

      {/* Expanded Bio Modal */}
      {showExpandedBio && data.fullBio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-xl overflow-hidden">
            <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Biografie</h2>
              <button
                onClick={() => setShowExpandedBio(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="prose prose-lg max-w-none">
                  <div className="text-gray-800">{parseFormattedText(data.fullBio)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && youtubeId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                className="absolute inset-0 w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}